import React, { useRef, useState } from 'react';
import {
  FormField,
  TextInput,
  Box,
  RadioLabelled,
  Button,
  SwitchLabelled,
  TextArea,
  Text,
} from '@compono/ui';
import { FormFooter } from 'src/components/Forms';
import history from 'lib/history';
import { Sso } from '../queries';
import { v4 as uuidV4 } from 'uuid';
import { omitNull } from 'src/utils';
import { CloseOutlineIcon } from '@compono/ui-icons';
import DeleteConfirmation from 'src/components/Forms/DeleteConfirmation';
import { showAlert } from 'src/components/AlertUtils';

export type NewSso = Omit<Sso, 'id'>;

const resolveMetadataType = (file: string, url: string) => {
  if (file) return 'upload';
  if (url) return 'url';
  return 'upload';
};

const getDefaultValue = (defaultValue: Partial<Sso> = {}): NewSso => ({
  enabled: defaultValue.enabled ?? true,
  name: defaultValue.name ?? '',
  type: defaultValue.type ?? 'SAML',
  entityId: defaultValue.entityId ?? '',
  acsUrl: defaultValue.acsUrl ?? '',
  logoutUrl: defaultValue.logoutUrl ?? '',
  metadataType: resolveMetadataType(
    defaultValue.metadataFile ?? '',
    defaultValue.metadataUrl ?? ''
  ),
  metadataFile: defaultValue.metadataFile ?? '',
  metadataUrl: defaultValue.metadataUrl ?? '',
  attributeMapping: defaultValue.attributeMapping ?? {},
  autoSignup: defaultValue.autoSignup ?? true,
  encryptedResponses: defaultValue.encryptedResponses ?? false,
  replaceLoginPage: defaultValue.replaceLoginPage ?? false,
  encryptionCertificate: defaultValue.encryptionCertificate ?? ``,
});

const borderlessInputSx = {
  '& input': {
    border: 'none !important',
  },
};
const containerBgGraySx = {
  backgroundColor: 'rgb(244, 244, 244)',
};

const validate = (input: Partial<NewSso>) => {
  let errors: any = {};
  if (!input.name || !input.name.trim()) errors.name = 'this is a required field';
  if (input.enabled && !input.metadataFile && !input.metadataUrl) {
    errors.metadata = `At least 'Metadata URL' or 'Metadata File' must be specified`;
  }
  return Object.keys(errors).length > 0 ? errors : undefined;
};

type Props = {
  defaultValue?: Partial<Sso>;
  onSubmit: (formValue: NewSso, id?: string) => void;
  onDelete?: (id: string) => void;
  saving?: boolean;
  orgId?: string;
};

const OrganisationSsoSettingsForm = ({
  defaultValue = {},
  onSubmit,
  onDelete,
  saving,
  orgId,
}: Props) => {
  const [formValue, setFormValue] = useState<NewSso>(getDefaultValue(defaultValue));
  const [newMapKey, setNewMapKey] = useState('');
  const [newMapValue, setNewMapValue] = useState('');
  const [metaFileName, setMetaFileName] = useState(() =>
    defaultValue.metadataFile ? `metadata_${uuidV4()}.xml` : ''
  );
  const [metaFileLink, setMetaFileLink] = useState(() =>
    defaultValue.metadataFile
      ? URL.createObjectURL(new Blob([defaultValue.metadataFile], { type: 'application/xml' }))
      : ''
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const nameInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (field: keyof NewSso, value: any) => {
    setFormValue(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleAddMapping = () => {
    const key = newMapKey.trim();
    const value = newMapValue.trim();
    if (!key || !value) return;
    setFormValue(prev => ({
      ...prev,
      attributeMapping: { ...prev.attributeMapping, [key]: value },
    }));
    setNewMapKey('');
    setNewMapValue('');
  };
  const handleRemoveMapping = (key: string) => {
    setFormValue(prev => {
      const map = { ...prev.attributeMapping };
      delete map[key];
      return { ...prev, attributeMapping: map };
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.name.endsWith('.xml')) {
      const reader = new FileReader();
      reader.onload = event => {
        const xmlContent = event.target?.result as string;
        handleChange('metadataFile', xmlContent);
        setMetaFileName('');
        setMetaFileLink('');
      };
      reader.readAsText(file);
    } else {
      handleChange('metadataFile', '');
      setMetaFileName('');
      setMetaFileLink('');
    }
  };

  const downloadMetadataFile = () => {
    if (metaFileLink && metaFileName) {
      const a = document.createElement('a');
      a.href = metaFileLink;
      a.download = metaFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const downloadCertificate = () => {
    const blob = new Blob([formValue.encryptionCertificate || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'certificate.crt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = validate(formValue);
    if (formValue.metadataType === 'upload') {
      delete formValue.metadataUrl;
    } else if (formValue.metadataType === 'url') {
      delete formValue.metadataFile;
    }
    if (validationErrors) {
      setErrors(validationErrors);

      if (validationErrors.name && nameInputRef.current) {
        nameInputRef.current.focus();
        return;
      }

      if (validationErrors.metadata) {
        showAlert({
          title: 'Error while creating SSO settings',
          content: validationErrors.metadata,
          alertType: 'error',
        });
      }
      return;
    }

    setErrors({});
    onSubmit(omitNull(formValue, true), defaultValue.id);
  };
  return (
    <form onSubmit={handleSubmit}>
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 20,
          }}
        >
          <Text>Enable SSO</Text>
          <SwitchLabelled
            checked={formValue.enabled}
            onChange={() => handleChange('enabled', !formValue.enabled)}
            size="sm"
          />
        </Box>

        {formValue.enabled && (
          <>
            <FormField fieldId="name" label="SSO Connection Name" error={errors.name}>
              <TextInput
                ref={nameInputRef}
                sx={{ ...borderlessInputSx }}
                name="name"
                value={formValue.name}
                onChange={e => handleChange('name', e.target.value)}
                maxLength={50}
                invalid={!!errors.name}
              />
            </FormField>
            <Box sx={{ mt: errors.name ? undefined : -12, mb: 20 }}>
              Type
              <RadioLabelled label="SAML" checked readOnly />
            </Box>
            <Box sx={{ mb: 20 }}>
              Entity ID
              <TextInput
                name="entityId"
                value={formValue.entityId}
                readOnly
                sx={{ ...containerBgGraySx, ...borderlessInputSx }}
              />
            </Box>
            <Box sx={{ mb: 20 }}>
              ACS URL
              <TextInput
                name="acsUrl"
                value={formValue.acsUrl}
                readOnly
                sx={{ ...containerBgGraySx, ...borderlessInputSx }}
              />
            </Box>
            <Box sx={{ mb: 20 }}>
              Logout URL
              <TextInput
                name="logoutUrl"
                value={formValue.logoutUrl}
                readOnly
                sx={{ ...containerBgGraySx, ...borderlessInputSx }}
              />
            </Box>
            <Box sx={{ mb: 20 }}>
              Metadata Document
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 10, mb: 10 }}>
                <RadioLabelled
                  name="metadataType"
                  label="Upload file"
                  checked={formValue.metadataType === 'upload'}
                  onChange={() => handleChange('metadataType', 'upload')}
                  sx={{ mr: 40 }}
                />
                <RadioLabelled
                  name="metadataType"
                  label="Endpoint URL"
                  checked={formValue.metadataType === 'url'}
                  onChange={() => handleChange('metadataType', 'url')}
                />
              </Box>
              {formValue.metadataType === 'upload' ? (
                <>
                  <Box sx={{ ...borderlessInputSx, mt: 3 }}>
                    <input
                      type="file"
                      accept=".xml"
                      onChange={handleFileChange}
                      style={{ width: '100%' }}
                    />
                  </Box>
                  {metaFileName && metaFileLink && (
                    <div style={{ marginTop: 2, fontSize: 16, color: '#2055C0' }}>
                      <a
                        href={metaFileLink}
                        download={metaFileName}
                        style={{
                          color: '#2055C0',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                        onClick={e => {
                          e.preventDefault();
                          downloadMetadataFile();
                        }}
                      >
                        {metaFileName}
                      </a>
                    </div>
                  )}
                </>
              ) : (
                <TextInput
                  sx={{ ...borderlessInputSx, mt: 3 }}
                  name="metadataUrl"
                  value={formValue.metadataUrl}
                  onChange={(e: { target: { value: any } }) =>
                    handleChange('metadataUrl', e.target.value)
                  }
                  placeholder="Metadata URL"
                />
              )}
            </Box>
            <Box sx={{ mb: 20 }}>
              Attribute Mapping
              <Box>
                {Object.entries(formValue.attributeMapping).map(([k, v]) => (
                  <Box
                    key={k}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      mb: 10,
                    }}
                  >
                    <TextInput value={k} readOnly sx={{ ...borderlessInputSx, flex: 1 }} />
                    <TextInput value={v} readOnly sx={{ ...borderlessInputSx, flex: 1 }} />
                    <Box sx={{ flex: '0 0 120px', display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        look="secondary"
                        type="button"
                        tone="critical"
                        onClick={() => handleRemoveMapping(k)}
                        style={{ width: '100%' }}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Box>
                ))}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    mb: 10,
                  }}
                >
                  <TextInput
                    value={newMapKey}
                    onChange={e => setNewMapKey(e.target.value)}
                    placeholder="User Attribute"
                    sx={{ flex: 1, ...borderlessInputSx }}
                  />
                  <TextInput
                    value={newMapValue}
                    onChange={e => setNewMapValue(e.target.value)}
                    placeholder="SAML Attribute"
                    sx={{ flex: 1, ...borderlessInputSx }}
                  />
                  <Box sx={{ flex: '0 0 120px', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      look="primary"
                      type="button"
                      onClick={handleAddMapping}
                      disabled={!(newMapKey.trim() && newMapValue.trim())}
                      style={{ width: '100%' }}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 20,
                marginTop: 10,
              }}
            >
              <Text>Encrypted Response</Text>
              <SwitchLabelled
                checked={formValue.encryptedResponses}
                onChange={() => handleChange('encryptedResponses', !formValue.encryptedResponses)}
                size="sm"
              />
            </Box>
            {formValue.encryptedResponses && (
              <Box sx={{ mb: 3 }}>
                <FormField fieldId="encryptionCertificate" label="Signing Certificate">
                  <TextArea
                    name="encryptionCertificate"
                    value={formValue.encryptionCertificate}
                    readOnly
                    disabled
                    size="sm"
                    sx={{ ...borderlessInputSx, ...containerBgGraySx }}
                  />
                  {formValue.encryptionCertificate && (
                    <Button
                      type="button"
                      look="primary"
                      size="sm"
                      style={{ marginTop: 8, width: 130 }}
                      onClick={downloadCertificate}
                    >
                      Download as .crt
                    </Button>
                  )}
                </FormField>
              </Box>
            )}
          </>
        )}
        <FormFooter>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
            {defaultValue.id && (
              <Button type="button" tone="critical" onClick={() => setIsDeleteDialogOpen(true)}>
                Delete
              </Button>
            )}
            <Button disabled={saving} type="submit">
              Save
            </Button>
          </Box>
          <Button
            type="button"
            iconLeft={CloseOutlineIcon}
            look="secondary"
            onClick={() => history.changeDomainTo(`/organisations/${orgId}/sso`)}
            sx={{ width: 'auto' }}
          >
            Cancel
          </Button>
        </FormFooter>
        {isDeleteDialogOpen && (
          <DeleteConfirmation
            title="Delete SSO"
            mainDescription="Delete Single Sign-On connection?"
            subDescription="Are you sure you want to delete the Single Sign-On connection?"
            confirmButtonTone="critical"
            confirmText="Delete"
            onSubmit={() => {
              setIsDeleteDialogOpen(false);
              if (onDelete && defaultValue.id) {
                onDelete(defaultValue.id);
              }
            }}
            onClose={() => setIsDeleteDialogOpen(false)}
          />
        )}
      </Box>
    </form>
  );
};

export default OrganisationSsoSettingsForm;
